import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import { UserCircle, LayoutDashboard, Settings, LogOut, Coins } from 'lucide-react';
import { Menu, Transition } from '@headlessui/react';
import { supabase } from '../lib/supabase';
import CoinsDisplay from './CoinsDisplay';
import UserAvatar from './UserAvatar';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  onAuthClick: () => void;
  session: Session | null;
  coinsRefreshTrigger?: number; // Nuevo prop para forzar la actualización
}

export function Header({ onAuthClick, session, coinsRefreshTrigger = 0 }: HeaderProps) {
  // Estado local para forzar la actualización de las monedas
  const [refreshCoinsCounter, setRefreshCoinsCounter] = useState(0);

  // Obtener el navigate para redireccionar
  const navigate = useNavigate();

  // Combinar el trigger externo con el contador local
  const combinedRefreshTrigger = coinsRefreshTrigger + refreshCoinsCounter;

  // Función para forzar la actualización de las monedas
  const refreshCoins = () => {
    setRefreshCoinsCounter(prev => prev + 1);
  };

  const getUserDisplayName = () => {
    if (!session?.user) return '';
    return session.user.email || session.user.user_metadata?.full_name || '';
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Efecto de fondo glassmorphism */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-950/85 to-indigo-950/80 backdrop-blur-md border-b border-blue-500/20 shadow-lg shadow-blue-500/10" />

      {/* Contenedor con el mismo ancho máximo que el main */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center text-white hover:opacity-80 transition-opacity w-32 md:w-36 flex-shrink-0"
          >
            <div className="flex items-center justify-center w-full">
              <img src="/img/cvmaker.png" alt="CvMaker" className="h-12 md:h-14 object-contain" />
            </div>
          </Link>

          {/* Navegación - Solo visible cuando NO hay sesión */}
          {!session && (
            <nav className="hidden md:flex space-x-6">
              <Link
                to="/"
                className="text-purple-100 text-sm font-medium hover:text-white transition-colors"
              >
                Inicio
              </Link>
              <Link
                to="/features"
                className="text-purple-100 text-sm font-medium hover:text-white transition-colors"
              >
                Características
              </Link>
              <Link
                to="/pricing"
                className="text-purple-100 text-sm font-medium hover:text-white transition-colors"
              >
                Precios
              </Link>
              <Link
                to="/testimonials"
                className="text-purple-100 text-sm font-medium hover:text-white transition-colors"
              >
                Testimonios
              </Link>
              <Link
                to="/contact"
                className="text-purple-100 text-sm font-medium hover:text-white transition-colors"
              >
                Contacto
              </Link>
            </nav>
          )}

          {/* Sección derecha: Coins + Botón de autenticación */}
          <div className="flex items-center space-x-4">
            {/* Mostrar coins solo si hay sesión */}
            {session?.user && (
              <CoinsDisplay
                user={session.user}
                refreshTrigger={combinedRefreshTrigger}
              />
            )}

            {/* Botón de autenticación */}
            {!session ? (
              <button
                onClick={onAuthClick}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium 
                         hover:from-blue-500 hover:to-purple-500 transition-all transform hover:scale-[1.02]
                         relative group overflow-hidden"
              >
                <span className="relative z-10">Acceder</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/50 to-purple-600/50 blur opacity-0 
                              group-hover:opacity-100 transition-opacity" />
              </button>
            ) : (
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center justify-center rounded-full 
                                       hover:opacity-80
                                       transition-all duration-300
                                       w-12 h-12 group">
                  <UserAvatar user={session?.user} size="sm" className="w-11 h-11 object-cover rounded-full" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-150"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg 
                                        bg-gradient-to-br from-purple-900/50 to-blue-900/50 
                                        backdrop-blur-xl 
                                        border border-white/20
                                        shadow-lg shadow-purple-500/20">
                    <div className="p-1 space-y-1">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/dashboard"
                            className={`${active ? 'bg-white/10' : ''
                              } flex items-center space-x-2 px-4 py-2 rounded-md text-white text-sm`}
                          >
                            <LayoutDashboard className="w-4 h-4" />
                            <span>Dashboard</span>
                          </Link>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/coins"
                            className={`${active ? 'bg-white/10' : ''
                              } flex items-center space-x-2 px-4 py-2 rounded-md text-white text-sm`}
                            onClick={refreshCoins} // Refrescar monedas al hacer clic
                          >
                            <Coins className="w-4 h-4" />
                            <span>Mis Monedas</span>
                          </Link>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/account"
                            className={`${active ? 'bg-white/10' : ''
                              } flex items-center space-x-2 px-4 py-2 rounded-md text-white text-sm`}
                          >
                            <Settings className="w-4 h-4" />
                            <span>Mi Cuenta</span>
                          </Link>
                        )}
                      </Menu.Item>

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={handleSignOut}
                            className={`${active ? 'bg-white/10' : ''
                              } flex items-center space-x-2 px-4 py-2 rounded-md text-white text-sm w-full`}
                          >
                            <LogOut className="w-4 h-4" />
                            <span>Cerrar Sesión</span>
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}